/* ============================================================
   <Logo> — Route and Route brand mark
   Single source of truth in @rr/ui. Apps consume this component;
   they never re-import or re-tint the asset directly.

   The mark is the redesigned raster logo (white mark on a black
   field), rendered inside a rounded badge so the black field reads
   as intentional brand chrome rather than a stray rectangle. Every
   app uses the SAME mark — no per-app tinting anymore.

   For an app that needs a label (e.g. the admin panel), use
   <LogoLockup label="ADMIN" />.
   ============================================================ */

import * as React from 'react'
import { cn } from '../../utils'
// The asset resolves to a plain URL string under Vite (tenant-web, rr-admin)
// but to a `{ src }` object (StaticImageData) under Next.js (books, portals,
// freight). Normalise both to a string so the same component works everywhere.
import logoAsset from '../../brand/logo.jpeg'

const LOGO_SRC: string =
  typeof logoAsset === 'string'
    ? logoAsset
    : (logoAsset as { src: string }).src

/** Kept for back-compat with existing call sites; tone is now ignored —
 *  the logo renders the same in every app. */
export type LogoTone = 'primary' | 'mono-dark' | 'mono-light' | 'current'

type LogoProps = Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> & {
  /** @deprecated The mark no longer tints — kept so old call sites compile. */
  tone?: LogoTone
  /** Visual size in px. Defaults to 32 (matches topbar). */
  size?: number
  /** Accessible label. Defaults to "Route and Route". */
  label?: string
}

export function Logo({
  size = 32,
  label = 'Route and Route',
  className,
  tone: _tone,
  ...props
}: LogoProps) {
  return (
    <span
      role="img"
      aria-label={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden bg-black',
        className,
      )}
      style={{ width: size, height: size, borderRadius: Math.round(size * 0.22) }}
      {...props}
    >
      <img
        src={LOGO_SRC}
        alt=""
        width={size}
        height={size}
        className="h-full w-full object-cover"
        draggable={false}
      />
    </span>
  )
}

/** Lockup: mark + wordmark (or an app label like "ADMIN"), for topbars. */
export function LogoLockup({
  size = 28,
  className,
  brandText = 'Route and Route',
  /** Optional app tag rendered as a chip after the mark (e.g. "ADMIN"). */
  label,
  /** Hide the wordmark and show only the mark (+ optional label). */
  markOnly = false,
}: {
  tone?: LogoTone
  size?: number
  className?: string
  brandText?: string
  label?: string
  markOnly?: boolean
}) {
  return (
    <span className={cn('inline-flex items-center gap-2 font-semibold', className)}>
      <Logo size={size} />
      {!markOnly && <span className="tracking-tight">{brandText}</span>}
      {label && (
        <span className="rounded-[6px] bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      )}
    </span>
  )
}
